"use client";

import {JSX, useState} from "react";

import SmartImage from "@/shared/ui/smart-image";

import classNames from "classnames";

type SliderImage = {url: string; alt: string};

interface StewardshipSliderProps {
    images: Array<SliderImage>;
    logo?: SliderImage;
}

/**
 * Image slider for the Franchise Stewardship section: one image at a time,
 * arrows on the sides, dots at the bottom and the franchise logo on top.
 */
export function StewardshipSlider({images, logo}: StewardshipSliderProps): JSX.Element {
    const [index, setIndex] = useState(0);
    const hasMany = images.length > 1;

    const go = (next: number): void => setIndex((next + images.length) % images.length);

    return (
        <div className="franchise-stewardship__slider">
            <div className="franchise-stewardship__slides">
                {images.map((image, i) => (
                    <div
                        key={image.url}
                        className={classNames("franchise-stewardship__slide", {"franchise-stewardship__slide--active": i === index})}
                        aria-hidden={i !== index}
                    >
                        <SmartImage src={image.url} alt={image.alt} fill sizes="(max-width: 767px) 100vw, 520px" style={{objectFit: "cover"}} />
                    </div>
                ))}

                {logo && (
                    <div className="franchise-stewardship__logo">
                        <SmartImage src={logo.url} alt={logo.alt} fill sizes="240px" style={{objectFit: "contain"}} />
                    </div>
                )}

                {hasMany && (
                    <div className="franchise-stewardship__dots">
                        {images.map((image, i) => (
                            <button
                                key={image.url}
                                type="button"
                                aria-label={`${i + 1} / ${images.length}`}
                                className={classNames("franchise-stewardship__dot", {"franchise-stewardship__dot--active": i === index})}
                                onClick={() => setIndex(i)}
                            />
                        ))}
                    </div>
                )}
            </div>

            {hasMany && (
                <>
                    <button
                        type="button"
                        aria-label="Previous"
                        className="franchise-stewardship__arrow franchise-stewardship__arrow--prev"
                        onClick={() => go(index - 1)}
                    >
                        ←
                    </button>
                    <button
                        type="button"
                        aria-label="Next"
                        className="franchise-stewardship__arrow franchise-stewardship__arrow--next"
                        onClick={() => go(index + 1)}
                    >
                        →
                    </button>
                </>
            )}
        </div>
    );
}
